import type { ValueFormat } from '@forgd/formatter'
import type { AxisConfigInterface, StackedBarConfigInterface, XYContainerConfigInterface } from '@unovis/ts'
import { formatValue } from '@forgd/formatter'

// ---------------------------------------------------------------------------------------------------------------------
// types
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Chart axis function accessor
 */
export type UiChartAccessor<T extends object> = (d: T, i: number) => number

/**
 * Chart X axis string or function accessor
 *
 * Should be:
 *
 * - a single datum key (i.e. `"x"` or `"month"`)
 * - a function that receives a datum and returns a single number
 *
 * > Note: Passing an attribute (i.e. "x") assumes values are in the **same** object
 */
type UiChartXAccessor<T extends object> = NumericKey<T> | UiChartAccessor<T>

type NumericKey<T> = keyof { [K in keyof T as T[K] extends number | null | undefined ? K : never]: T[K] }

/**
 * Chart Y axis string or function accessor
 *
 * Supports single or stacked values
 *
 * Should be:
 *
 * - a single datum key (i.e. `"y"`) which points to a property that holds an array of numbers
 * - an array of keys (i.e. `["foo", "bar", ...]`) that points to properties on each record
 * - a single function that receives a datum and returns an array of numbers
 * - an array of functions that each receives a datum and returns a single number
 */
type UiChartYAccessor<T extends object> = keyof T | UiChartAccessor<T> | Array<keyof T | UiChartAccessor<T>>

/**
 * Extended chart axis configuration
 *
 * @deprecated potentially, due to slippery-slope of type augmentation
 * @see https://linear.app/forgd/issue/DEV-4640/core-chart-wrapper-component-improvements
 *
 * > See `makeAxisConfig()` for full property extraction
 */
export type UiChartAxisConfigInterface<T> = Omit<Partial<AxisConfigInterface<T>>, 'tickFormat'> & {
  tickFormat?: AxisConfigInterface<T>['tickFormat'] | ValueFormat
  tickThreshold?: number
}

/**
 * Props for StackedBarChart
 */
export interface StackedBarChartProps<T extends object> {
  // data
  title?: string
  data: T[]
  x?: UiChartXAccessor<T>
  y?: UiChartYAccessor<T>

  // proxies
  xyContainer?: Partial<XYContainerConfigInterface<T>>
  stackedBar?: Partial<StackedBarConfigInterface<T>>
  xAxis?: Partial<UiChartAxisConfigInterface<T>> | false
  yAxis?: Partial<UiChartAxisConfigInterface<T>> | false
}

// ---------------------------------------------------------------------------------------------------------------------
// utilities
// ---------------------------------------------------------------------------------------------------------------------

function field(d: object, key: string): unknown {
  return (d as Record<string, unknown>)[key]
}

function toNumber(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Get a datum (point) in the data array (defaults to last)
 */
export function getDatum<T extends object>(data: T[], index = -1): T | null {
  const point = data.at(index)
  if (typeof point === 'object' && point !== null) {
    return point
  }
  return null
}

/**
 * Generate Unovis X accessors
 *
 * - handles string and function accessor props
 * - guesses common { x, y[] } attributes from shape of data
 *    - { month: number, a: number, b: number, ... }
 *    - { x: number, y: number[] }
 */
export function makeXAccessors<T extends object>(x: UiChartXAccessor<T> | undefined) {
  if (x) {
    // string attribute or accessor
    return typeof x === 'function' ? x : (d: T) => toNumber(field(d, String(x)))
  }

  // fallback / guess default
  return (d: T, i: number) => toNumber(field(d, 'x')) ?? toNumber(field(d, 'month')) ?? i
}

/**
 * Generate Unovis Y accessors
 *
 * - handles string and function accessor props
 * - guesses common { x, y[] } attributes from shape of data
 *    - { month: number, a: number, b: number, ... }
 *    - { x: number, y: number[] }
 *
 * **IMPORTANT**: ensure a real array of accessors is passed; if generated, rather than arbitrary accessor props
 */
export function makeYAccessors<T extends object>(
  data: T[],
  y: UiChartYAccessor<T> | undefined,
  x: UiChartXAccessor<T> | undefined,
): UiChartAccessor<T> | UiChartAccessor<T>[] {
  // variables
  const p = getDatum(data)

  // helpers
  const getChildValues = (p: T, y: string): UiChartAccessor<T>[] => {
    const values = field(p, y)
    if (Array.isArray(values)) {
      return values.map((_v, i) => (d: T) => {
        const child = field(d, y)
        return Array.isArray(child) ? Number(child[i] ?? 0) : 0
      })
    }
    if (isRecord(values)) {
      return Object.keys(values).map((key) => (d: T) => {
        const child = field(d, y)
        return isRecord(child) ? Number(child[key] ?? 0) : 0
      })
    }
    return []
  }

  const getSiblingValues = (p: T, x: string): UiChartAccessor<T>[] =>
    Object.keys(p)
      .filter((k) => k !== x)
      .map(
        (k: string) =>
          (d: T): number =>
            Number(d[k as keyof T] ?? 0),
      )

  // first data point is an object
  if (p) {
    // y accessor is passed in props, use it directly
    if (y) {
      // y points at array or object
      if (typeof y === 'string' && isRecord(field(p, y))) {
        return getChildValues(p, y)
      }

      // array of strings or accessors
      if (Array.isArray(y)) {
        return y.map((v) => {
          return typeof v === 'function' ? (v as UiChartAccessor<T>) : (d: T) => Number(d[v as keyof T] ?? 0)
        })
      }

      // single string or accessor
      return typeof y === 'function' ? (y as UiChartAccessor<T>) : (d: T) => Number(d[y as keyof T] ?? 0)
    }

    // no y-accessor passed (attempt to guess)
    // TODO consider determining these values first, then re-setting x and y
    // so we don't need to write the parsing logic in two places
    else {
      // "y" contains values
      if (Array.isArray(field(p, 'y'))) {
        return getChildValues(p, 'y')
      }

      // "month" is an object property
      if ('month' in p) {
        return getSiblingValues(p, 'month')
      }

      // x accessor is a string
      if (typeof x === 'string') {
        return getSiblingValues(p, x)
      }
    }
  }

  // if y is not passed, we assume it's a single value (so, unstacked)
  return (d: T) => Number(field(d, 'y') ?? 0)
}

// ---------------------------------------------------------------------------------------------------------------------
// axes
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Helper to compute min and max Y values from data and accessors
 *
 * @internal
 * @param data
 * @param accessors
 */
function computeYBounds<T extends object>(
  data: T[],
  accessors: UiChartYAccessor<T> | UiChartYAccessor<T>[],
): [number, number] {
  let minNegative = 0
  let maxPositive = 0

  for (let i = 0; i < data.length; i++) {
    const datum = data[i]!
    let positiveTotal = 0
    let negativeTotal = 0

    const accessorsArray = Array.isArray(accessors) ? accessors : [accessors]

    accessorsArray.forEach((accessor) => {
      const value =
        typeof accessor === 'function'
          ? accessor(datum, i)
          : typeof accessor === 'string'
            ? Number(datum[accessor] ?? 0) || 0
            : 0

      if (value >= 0) {
        positiveTotal += value
      } else {
        negativeTotal += value
      }
    })

    if (negativeTotal < minNegative) {
      minNegative = negativeTotal
    }
    if (positiveTotal > maxPositive) {
      maxPositive = positiveTotal
    }
  }

  // tidy tiny float noise but DO NOT round away sub-1 ranges
  const EPS = 1e-12
  if (Math.abs(minNegative) < EPS) {
    minNegative = 0
  }
  if (Math.abs(maxPositive) < EPS) {
    maxPositive = 0
  }

  return [minNegative, maxPositive]
}

/**
 * Helper to round a value to a "nice" number (1, 2, 5, 10, etc)
 *
 * @internal
 * @param value
 */
export function roundToNice(value: number): number {
  const absValue = Math.abs(value)
  if (absValue === 0) {
    return 0
  }

  const magnitude = Math.floor(Math.log10(absValue))
  const power = 10 ** magnitude
  const normalized = absValue / power

  let nice: number
  if (normalized <= 1.5) {
    nice = (Math.ceil(normalized * 2) / 2) * power
  } else if (normalized <= 3) {
    nice = Math.ceil(normalized) * power
  } else if (normalized <= 7) {
    nice = Math.ceil(normalized / 5) * 5 * power
  } else {
    nice = Math.ceil(normalized / 10) * 10 * power
  }

  return value < 0 ? -nice : nice
}

/**
 * Helper to round a rough step size to a "nice" number (1, 2, 5, 10, etc)
 *
 * @internal
 * @param roughStep
 */
function roundStepToNice(roughStep: number): number {
  const magnitude = Math.floor(Math.log10(roughStep))
  const power = 10 ** magnitude
  const normalized = roughStep / power
  if (normalized <= 1) {
    return power
  }
  if (normalized <= 2) {
    return 2 * power
  }
  if (normalized <= 5) {
    return 5 * power
  }
  return 10 * power
}

/**
 * Helper to round a value to a "nice" number based on target divisions
 *
 * @internal
 * @param value
 * @param divisions
 */
function roundToDivisor(value: number, divisions: number): number {
  const absValue = Math.abs(value)
  if (absValue === 0) {
    return 0
  }

  const targetStep = absValue / divisions
  const magnitude = Math.floor(Math.log10(targetStep))
  const power = 10 ** magnitude
  const candidates = [1, 2, 2.5, 5, 10].map((b) => b * power)

  let bestStep = candidates[0]!
  let bestScore = Infinity

  for (const step of candidates) {
    const stepsNeeded = Math.ceil(absValue / step)
    const maxValue = step * stepsNeeded
    const wastedSpace = maxValue - absValue
    const divisionDifference = Math.abs(stepsNeeded - divisions)
    const score = wastedSpace + divisionDifference * absValue * 0.1
    if (score < bestScore) {
      bestScore = score
      bestStep = step
    }
  }

  const stepsNeeded = Math.ceil(absValue / bestStep)
  const rounded = bestStep * stepsNeeded
  return value < 0 ? -rounded : rounded
}

/**
 * Calculate the correct y-domain value
 *
 * - sums stacked bar values
 * - handles negative and positive values
 * - rounds to the nearest integer
 * - optionally rounds bounds to nice numbers for tick display
 */
export function getYDomain<T extends object>(
  data: T[],
  accessors: UiChartYAccessor<T> | UiChartYAccessor<T>[],
  roundBounds: boolean = false,
): [number, number] {
  let [minNegative, maxPositive] = computeYBounds(data, accessors)

  if (roundBounds) {
    minNegative = roundToNice(minNegative)
    maxPositive = roundToNice(maxPositive)
  }

  return [minNegative, maxPositive]
}

/**
 * Calculate y-domain & y-tickValues for stacked bar charts
 *
 * - sums stacked bar values
 * - handles negative and positive values
 * - rounds to the nearest integer
 * - optionally rounds bounds to nice numbers for tick display
 * - returns yDomain and tickValues for direct use with Unovis
 *
 * @usage
 *
 *  - run function with data and accessors
 *  - assign `yDomain` to `yAxis.yDomain`
 *  - assign `tickValues` to `xyContainer.tickValues`
 *
 * Rationale:
 *
 * In order for Unovis to marry up the yDomain and tickValues correctly,
 * we need to calculate both values in one go, and pass them to the charts.
 *
 * Additionally, we want to ensure that the tickValues are "nice" numbers,
 * and ensure that 0 is included when we have both negative and positive values.
 *
 * Note:
 *
 * This function was iterated over several rounds using AI; feel free to improve as needed!
 *
 * @param data          An array of Datum objects
 * @param accessors     One or more Y accessors
 * @param roundBounds   A hint to round the bounds to nice numbers. Use:
 *                        - true for default rounding (optimizes for tick values)
 *                        - a number to hint the number of divisions (optimizes for spacing)
 */
export function getYAttrs<T extends object>(
  data: T[],
  accessors: UiChartYAccessor<T> | UiChartYAccessor<T>[],
  roundBounds: boolean | number = true,
): {
  yDomain: [number, number]
  tickValues: number[]
} {
  let [minNegative, maxPositive] = computeYBounds(data, accessors)

  if (minNegative === 0 && maxPositive === 0) {
    return {
      yDomain: [0, 0],
      tickValues: [0],
    }
  }

  let tickValues: number[] = []

  if (roundBounds) {
    if (typeof roundBounds === 'number') {
      const roundedMinNegative = roundToDivisor(minNegative, roundBounds)
      const roundedMaxPositive = roundToDivisor(maxPositive, roundBounds)

      const hasNegative = roundedMinNegative < 0
      const hasPositive = roundedMaxPositive > 0

      if (hasNegative && hasPositive) {
        const totalRange = roundedMaxPositive - roundedMinNegative
        const roughStep = totalRange / roundBounds
        const niceStep = roundStepToNice(roughStep)

        const stepsDown = Math.ceil(Math.abs(roundedMinNegative) / niceStep)
        const stepsUp = Math.ceil(roundedMaxPositive / niceStep)

        for (let i = -stepsDown; i <= stepsUp; i++) {
          tickValues.push(i * niceStep)
        }

        minNegative = -stepsDown * niceStep
        maxPositive = stepsUp * niceStep
      } else {
        const step = hasNegative ? Math.abs(roundedMinNegative) / roundBounds : roundedMaxPositive / roundBounds

        if (hasNegative) {
          for (let i = 0; i <= roundBounds; i++) {
            tickValues.push(roundedMinNegative + (Math.abs(roundedMinNegative) / roundBounds) * i)
          }
        } else {
          for (let i = 0; i <= roundBounds; i++) {
            tickValues.push(step * i)
          }
        }

        minNegative = roundedMinNegative
        maxPositive = roundedMaxPositive
      }
    } else {
      // boolean true: nice the bounds, generate nice ticks, then ALIGN DOMAIN TO TICKS
      minNegative = roundToNice(minNegative)
      maxPositive = roundToNice(maxPositive)

      const range = maxPositive - minNegative
      const roughStepSize = range / 5
      const niceStep = roundStepToNice(roughStepSize)

      const startTick = Math.floor(minNegative / niceStep) * niceStep
      const endTick = Math.ceil(maxPositive / niceStep) * niceStep

      for (let tick = startTick; tick <= endTick; tick += niceStep) {
        tickValues.push(tick)
      }

      // ensure yDomain matches the tick bounds
      minNegative = startTick
      maxPositive = endTick
    }
  } else {
    if (minNegative === maxPositive) {
      tickValues = [minNegative]
    } else {
      tickValues = [minNegative, maxPositive]
    }
  }

  // Final tidy: snap ticks and domain to sensible decimal places
  const stepGuess =
    tickValues.length > 1
      ? Math.abs(tickValues[1]! - tickValues[0]!)
      : maxPositive !== minNegative
        ? (maxPositive - minNegative) / 5
        : 0

  const dp = stepGuess ? Math.max(0, -Math.floor(Math.log10(stepGuess))) : 0
  const clampDp = (n: number) => Number(n.toFixed(Math.min(6, dp + 1)))

  tickValues = tickValues.map((n) => clampDp(n))
  const yDomain: [number, number] = [clampDp(minNegative), clampDp(maxPositive)]

  return {
    yDomain,
    tickValues,
  }
}

/**
 * Generate UnoVis Axis config
 *
 * - converts string `tickFormat` to function
 */
export function makeAxisConfig<T extends object>(
  attrs: Partial<UiChartAxisConfigInterface<T>> | false | undefined,
): AxisConfigInterface<T> {
  if (attrs) {
    const { tickFormat, ...rest } = attrs
    return {
      // pass back existing attributes
      ...rest,

      // allow string or function tickFormat
      tickFormat: typeof tickFormat === 'string' ? (tick: number | Date) => formatValue(tick, tickFormat) : tickFormat,
    }
  }
  return {}
}
